import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useQuizeStore } from "../../../stores/quizeStore";
import {
  formatAssessmentBonusSuffix,
  formatAssessmentScoreFraction,
  formatAssessmentScoreNumber,
  getAssessmentBaseScore,
} from "../../../utils/assessmentScore";
import { assessmentPhoneLandscapeQuery } from "../assessment-layout-styles";
import {
  containerStyle,
  circularContainerStyle,
  circularProgressStyle,
  circularProgressOverlayStyle,
  circularTextContainerStyle,
  circularTextStackStyle,
  circularTextStyle,
  circularBonusTextStyle,
  resultsContainerStyle,
  correctAnswersTextStyle,
} from "./styles";

export type AssessmentRunningScoreBreakdown = {
  mandatoryScore: number;
  mandatoryMaxScore: number;
  bonusQuestionScore: number;
  bonusQuestionMaxScore: number;
  userScore: number;
  gamificationBonusScore: number;
};

interface QuizResultsCircularProps {
  totalAnswers: number;
  isFinish?: boolean;
  totalMaxScore?: number;
  scoresEarned?: number;
  bonusScore?: number;
  hidePoints?: boolean;
  scoreBreakdown?: AssessmentRunningScoreBreakdown | null;
}

export const QuizResultsCircular: React.FC<QuizResultsCircularProps> = ({
  totalAnswers,
  isFinish = false,
  totalMaxScore: propTotalMaxScore,
  scoresEarned: propScoresEarned,
  bonusScore: propBonusScore = 0,
  hidePoints = false,
  scoreBreakdown = null,
}) => {
  const theme = useTheme();
  const isCompactTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isPhoneLandscape = useMediaQuery(assessmentPhoneLandscapeQuery);
  const isTabletPortrait = useMediaQuery(
    "(orientation: portrait) and (min-width: 600px) and (max-width: 900px)",
  );
  const { preassessmentData } = useQuizeStore();

  const hasContestBreakdown = Boolean(scoreBreakdown);
  const totalUserScore = hasContestBreakdown
    ? scoreBreakdown!.userScore
    : (propScoresEarned ?? preassessmentData.scores_earned);
  const totalMaxScore = hasContestBreakdown
    ? scoreBreakdown!.mandatoryMaxScore || (propTotalMaxScore ?? 5 * totalAnswers)
    : (propTotalMaxScore ?? 5 * totalAnswers);
  const bonusScore = hasContestBreakdown
    ? scoreBreakdown!.gamificationBonusScore
    : propBonusScore > 0
      ? propBonusScore
      : 0;
  const baseScore = hasContestBreakdown
    ? totalUserScore
    : getAssessmentBaseScore(totalUserScore, bonusScore);
  const scoreLabel =
    totalMaxScore > 0
      ? `${formatAssessmentScoreNumber(baseScore)}/${formatAssessmentScoreNumber(totalMaxScore)}`
      : formatAssessmentScoreNumber(baseScore);
  const bonusLabel = formatAssessmentBonusSuffix(bonusScore).trim();
  const hasBonus = bonusLabel.length > 0;
  const showWideFormat = scoreLabel.length >= 7 || hasBonus;
  const circleValue =
    totalMaxScore > 0
      ? Math.min(100, (baseScore / totalMaxScore) * 100)
      : 0;
  const showScoreDetails = isFinish;
  const showDetailedBreakdown =
    showScoreDetails &&
    hasContestBreakdown &&
    (scoreBreakdown!.bonusQuestionMaxScore > 0 ||
      scoreBreakdown!.mandatoryMaxScore > 0);

  const circleSize = isFinish
    ? hasBonus
      ? 96
      : 80
    : isPhoneLandscape
      ? hasBonus
        ? 64
        : showWideFormat
          ? 54
          : 52
      : hasBonus
        ? isTabletPortrait
          ? 84
          : isCompactTablet
            ? 76
            : 80
        : showWideFormat && isTabletPortrait
          ? 72
          : isCompactTablet
            ? 60
            : 66;

  return (
    <Box sx={containerStyle(isFinish)}>
      {hidePoints ? null : (
        <>
          <Box sx={circularContainerStyle}>
            <CircularProgress
              variant="determinate"
              value={circleValue}
              size={circleSize}
              thickness={6}
              sx={circularProgressStyle}
            />
            <CircularProgress
              variant="determinate"
              value={100}
              size={circleSize}
              thickness={5}
              sx={circularProgressOverlayStyle}
            />
            <Box sx={circularTextContainerStyle(theme)}>
              <Box sx={circularTextStackStyle}>
                <Typography
                  variant="caption"
                  component="div"
                  sx={circularTextStyle(
                    isFinish,
                    showWideFormat,
                    isCompactTablet,
                    hasBonus,
                  )}
                >
                  {scoreLabel}
                </Typography>
                {hasBonus ? (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={circularBonusTextStyle(isFinish, isCompactTablet)}
                  >
                    {bonusLabel}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
          {showScoreDetails ? (
            <Box sx={resultsContainerStyle(isFinish)}>
              <Typography
                variant="body2"
                sx={correctAnswersTextStyle(isFinish, theme)}
              >
                {hasContestBreakdown ? "Total user score" : "Points earned"}
              </Typography>
              {showDetailedBreakdown && scoreBreakdown ? (
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.25,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Mandatory score{" "}
                    {formatAssessmentScoreFraction(
                      scoreBreakdown.mandatoryScore,
                      scoreBreakdown.mandatoryMaxScore,
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Bonus questions{" "}
                    {formatAssessmentScoreFraction(
                      scoreBreakdown.bonusQuestionScore,
                      scoreBreakdown.bonusQuestionMaxScore,
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total user score{" "}
                    {formatAssessmentScoreNumber(scoreBreakdown.userScore)}
                  </Typography>
                  {scoreBreakdown.gamificationBonusScore > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      Speed/streak bonus +
                      {formatAssessmentScoreNumber(
                        scoreBreakdown.gamificationBonusScore,
                      )}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default QuizResultsCircular;
